import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Download, User, Filter, FileSpreadsheet, Eye } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { invoiceService } from '@/services/invoiceService';
import { configService } from '@/services/configService';
import { 
  InvoiceListParams
} from '@/types/invoice';

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export const FinanceReview = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedDonor, setSelectedDonor] = useState('');
  const [selectedGrantType, setSelectedGrantType] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedFinanceStatus, setSelectedFinanceStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  
  // API query filters
  const [filters, setFilters] = useState<InvoiceListParams>({
    page: 1,
    page_size: 10,
    month_year: getCurrentMonth()
  });

  const programs = [
    { value: '', label: 'All Programs' },
    { value: 'TBTP', label: 'The Billion Tree Project' },
    { value: 'WM', label: 'Waste Management' },
    { value: 'WE', label: 'Women Equity' },
    { value: 'LL', label: 'Learn Labs' },
    { value: 'SD', label: 'Sports for Development' },
    { value: 'RF', label: 'Regenerative Farming' },
    { value: 'YOGA', label: 'Yoga' },
  ];

  const grantTypeOptions = [
    { value: '', label: 'All Grant Types' },
    ...configService.getGrantTypes().map(grantType => ({
      value: grantType.id,
      label: grantType.label
    }))
  ];

  const stateOptions = [
    { value: '', label: 'All States' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Approved By PnC', label: 'Approved By PnC' },
    { value: 'Approved By BU', label: 'Approved By BU' },
  ];

  const pageSizeOptions = [
    { value: 10, label: '10 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' },
  ];

  const financeStatusOptions = [
    { value: '', label: 'All Finance Status' },
    { value: 'Ready', label: 'Ready' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Processing', label: 'Processing' },
    { value: 'Completed', label: 'Completed' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Rejected', label: 'Rejected' },
  ];

  // Update filters when search, page, program, donor, grant type, state, department, finance status, or page size changes
  useEffect(() => {
    const newFilters = {
      page: currentPage,
      page_size: pageSize,
      q: searchQuery?.trim() || undefined,
      program: selectedProgram || undefined,
      donor: selectedDonor ? selectedDonor: undefined,
      grant: selectedGrantType || undefined,
      state: selectedState || undefined,  // Use 'state' parameter with original values
      department: selectedDepartment || undefined,  // Use 'department' parameter with department ID
      month_year: selectedMonth || undefined,
      finance_status: selectedFinanceStatus || undefined
    };
    
    // console.log('FinanceReview - Setting filters:', newFilters);
    // console.log('FinanceReview - Selected department:', selectedDepartment);
    setFilters(newFilters);
  }, [searchQuery, currentPage, selectedProgram, selectedDonor, selectedGrantType, selectedState, selectedDepartment, selectedFinanceStatus, selectedMonth, pageSize]);

  // Fetch invoices using React Query - Finance section sees invoices approved by BU
  const {
    data: invoicesData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['finance-invoices', filters],
    queryFn: () => invoiceService.getInvoices('finance', filters),
    placeholderData: (previousData) => previousData,
  });

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleExportCSV = async () => {
    try {
      const filename = `finance-import-${selectedMonth}-${new Date().toISOString().split('T')[0]}.csv`;
      // Pass all current filters for export, remove pagination
      const exportFilters = {
        q: searchQuery?.trim() || undefined,
        program: selectedProgram || undefined,
        donor: selectedDonor ? selectedDonor: undefined,
        grant: selectedGrantType || undefined,
        state: selectedState || undefined,
        department: selectedDepartment || undefined,
        month_year: selectedMonth || undefined,
        finance_status: selectedFinanceStatus || undefined
      };
      await invoiceService.downloadFinanceCSV(exportFilters, filename);
      showSuccess('Finance CSV Export', 'Invoice data exported in finance software format successfully.');
    } catch (error) {
      showError('Export Failed', 'Failed to export finance data. Please try again.');
    }
  };

  const handleRegularExport = async () => {
    try {
      const filename = `finance-invoices-${selectedMonth}-${new Date().toISOString().split('T')[0]}.csv`;
      // Pass all current filters for export, remove pagination
      const exportFilters = {
        q: searchQuery?.trim() || undefined,
        program: selectedProgram || undefined,
        donor: selectedDonor ? selectedDonor: undefined,
        grant: selectedGrantType || undefined,
        state: selectedState || undefined,
        department: selectedDepartment || undefined,
        month_year: selectedMonth || undefined,
        finance_status: selectedFinanceStatus || undefined
      };
      await invoiceService.exportInvoices('finance', exportFilters, filename);
      showSuccess('Export Data', 'Finance invoice data exported successfully.');
    } catch (error) {
      showError('Export Failed', 'Failed to export finance data. Please try again.');
    }
  };


  const handleViewInvoice = async (invoiceId: number) => {
    try {
      await invoiceService.viewInvoice('finance',invoiceId);
    } catch (error) {
      showError('View Failed', 'Failed to open invoice. Please try again.');
    }
  };

  const handleFinanceStatusUpdate = async (invoiceId: number, newStatus: string) => {
    try {
      const updateData = [{
        id: invoiceId,
        finance_status: newStatus
      }];
      
      await invoiceService.updateInvoices('finance', updateData);
      
      // Refresh the invoice list after successful update
      await queryClient.invalidateQueries({ queryKey: ['finance-invoices'] });
      
      showSuccess('Status Updated', `Finance status updated to "${newStatus}" successfully.`);
    } catch (error) {
      console.error('Finance status update error:', error);
      showError('Update Failed', 'Failed to update finance status. Please try again.');
    }
  };

  const handleBulkFinanceStatusUpdate = async (newStatus: string) => {
    if (selectedInvoices.length === 0) {
      showError('No Selection', 'Please select invoices to update their finance status.');
      return;
    }

    try {
      const updateData = selectedInvoices.map(invoiceId => ({
        id: invoiceId,
        finance_status: newStatus
      }));
      
      await invoiceService.updateInvoices('finance', updateData);
      
      // Refresh the invoice list after successful update
      await queryClient.invalidateQueries({ queryKey: ['finance-invoices'] });
      
      // Clear selection after successful update
      setSelectedInvoices([]);
      
      showSuccess('Bulk Status Updated', `Finance status updated to "${newStatus}" for ${selectedInvoices.length} invoice(s) successfully.`);
    } catch (error) {
      console.error('Bulk finance status update error:', error);
      showError('Bulk Update Failed', 'Failed to update finance status for selected invoices. Please try again.');
    }
  };


  const getStatusBadgeClass = (_status: string) => {
    return 'badge badge-success'; // All invoices in finance are approved by BU
  };

  const toggleInvoiceSelection = (invoiceId: number) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (!invoicesData?.list) return;
    
    if (selectedInvoices.length === invoicesData.list.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoicesData.list.map(inv => inv.id));
    }
  };

  // Get donor name from config service
  const getDonorName = (donorId?: string) => {
    return configService.getDonorNameById(donorId);
  };

  // Get budget label from config service
  const getBudgetLabel = (budgetId?: string) => {
    return configService.getBudgetLabelById(budgetId);
  };

  // Get grant type label from config service
  const getGrantTypeLabel = (grantTypeId?: string) => {
    if (!grantTypeId) return 'Not specified';
    const grantType = configService.getGrantTypeById(grantTypeId);
    return grantType ? grantType.label : grantTypeId;
  };


  // Calculate summary statistics
  const totalAmount = invoicesData?.list?.reduce((sum, invoice) => sum + (invoice.invoice_value || 0), 0) || 0;
  const totalInvoices = invoicesData?.list?.length || 0;

  // Loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading invoices...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading invoices. Please try again.</div>
      </div>
    );
  }

  const invoices = invoicesData?.list || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            Finance Review - Step 3
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Review and download approved invoice data for financial processing. Use "Export for Finance Software" to get CSV in the format required for your finance system import.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <Button
            variant="secondary"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportCSV}
            title="Export CSV formatted for finance software import"
          >
            Export for Finance Software
          </Button>
          <Button
            icon={<FileSpreadsheet className="h-4 w-4" />}
            onClick={handleRegularExport}
            title="Export regular finance data"
          >
            Export Data
          </Button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedInvoices.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-rgf-gray-700">
                  {selectedInvoices.length} invoice(s) selected
                </span>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-rgf-gray-700">Update Finance Status:</label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkFinanceStatusUpdate(e.target.value);
                        e.target.value = ''; // Reset dropdown
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                    defaultValue=""
                  >
                    <option value="" disabled>Select Status</option>
                    {financeStatusOptions.slice(1).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedInvoices([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {totalInvoices > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileSpreadsheet className="h-8 w-8 text-rgf-primary-600" />
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-rgf-gray-500">Total Invoices</div>
                  <div className="text-2xl font-bold text-rgf-gray-900">{totalInvoices}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">₹</span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-rgf-gray-500">Total Amount</div>
                  <div className="text-2xl font-bold text-rgf-gray-900">₹{totalAmount.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">avg</span>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-rgf-gray-500">Average Amount</div>
                  <div className="text-2xl font-bold text-rgf-gray-900">
                    ₹{totalInvoices > 0 ? Math.round(totalAmount / totalInvoices).toLocaleString() : 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or employee ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button 
                variant="secondary" 
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 pt-4 border-t border-rgf-gray-200">
                {/* Month Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>

                {/* Program Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Program
                  </label>
                  <select
                    value={selectedProgram}
                    onChange={(e) => { setSelectedProgram(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {programs.map((program) => (
                      <option key={program.value} value={program.value}>
                        {program.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Donor Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Donor
                  </label>
                  <select
                    value={selectedDonor}
                    onChange={(e) => { setSelectedDonor(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">All Donors</option>
                    {configService.getDonors().map(donor => (
                      <option key={donor.id} value={donor.id}>
                        {donor.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Grant Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Grant Type
                  </label>
                  <select
                    value={selectedGrantType}
                    onChange={(e) => { setSelectedGrantType(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {grantTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* State Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => { setSelectedState(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {stateOptions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Department Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => { setSelectedDepartment(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">All Departments</option>
                    {configService.getDepartments().map(department => (
                      <option key={department.id} value={department.id}>
                        {department.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Finance Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Finance Status
                  </label>
                  <select
                    value={selectedFinanceStatus}
                    onChange={(e) => { setSelectedFinanceStatus(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {financeStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Clear Filters Button */}
            {showFilters && (
              <div className="pt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedMonth(getCurrentMonth());
                    setSelectedProgram('');
                    setSelectedDonor('');
                    setSelectedGrantType('');
                    setSelectedState('');
                    setSelectedDepartment('');
                    setSelectedFinanceStatus('');
                    setSearchQuery('');
                    setCurrentPage(1);
                    setPageSize(10);
                  }}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Table */}
      <div className="card">
        <div className="card-body p-0">
          {invoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No invoices available for finance review.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header w-12">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="table-header">Associate</th>
                    <th className="table-header">Program</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Donor</th>
                    <th className="table-header">Grant Type</th>
                    <th className="table-header">Budget ID</th>
                    <th className="table-header">Approval Details</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Finance Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {invoices.map((invoice) => {
                    return (
                      <tr key={invoice.id} className="hover:bg-rgf-gray-50">
                        <td className="table-cell">
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(invoice.id)}
                            onChange={() => toggleInvoiceSelection(invoice.id)}
                            className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-rgf-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-rgf-gray-900">
                                {invoice.name}
                              </div>
                              <div className="text-sm text-rgf-gray-500">
                                {invoice.employee_id} • {invoice.designation}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="badge badge-info">{invoice.program}</span>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-rgf-gray-900">
                            {invoice.invoice_value ? `₹${invoice.invoice_value.toLocaleString()}` : '-'}
                          </div>
                          <div className="text-xs text-rgf-gray-500">
                            {invoice.attended_days + (invoice.exceptions || 0)} days
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-rgf-gray-900">
                            <div className="font-medium">{getDonorName(invoice.donor_id)}</div>
                            {invoice.donor_type && (
                              <div className="text-xs text-rgf-gray-500">{invoice.donor_type}</div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-rgf-gray-900">
                            {getGrantTypeLabel(invoice.grant_type)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-rgf-gray-900">
                            {getBudgetLabel(invoice.budget_id)}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-xs text-rgf-gray-500">
                            <div><strong>PnC:</strong> {invoice.pnc_approved_by?.split('@')[0] || 'N/A'}</div>
                            <div><strong>BU:</strong> {invoice.bu_approved_by?.split('@')[0] || 'N/A'}</div>
                            {(invoice.pnc_remarks || invoice.bu_remarks) && (
                              <div className="mt-1">
                                {invoice.pnc_remarks && <div><strong>PnC:</strong> {invoice.pnc_remarks}</div>}
                                {invoice.bu_remarks && <div><strong>BU:</strong> {invoice.bu_remarks}</div>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className={getStatusBadgeClass(invoice.state || 'Approved By BU')}>
                            Ready for Payment
                          </span>
                        </td>
                        <td className="table-cell">
                          <select
                            value={invoice.finance_status || 'Ready'}
                            onChange={(e) => handleFinanceStatusUpdate(invoice.id, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                          >
                            {financeStatusOptions.slice(1).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="table-cell">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-800"
                            icon={<Eye className="h-3 w-3" />}
                            title="View Details"
                            onClick={() => handleViewInvoice(invoice.id)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {invoicesData && invoicesData.list.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing page {invoicesData.page} with {invoicesData.list.length} records
                  {selectedInvoices.length > 0 && (
                    <span className="ml-4 text-rgf-primary-600">
                      {selectedInvoices.length} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Page size:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={invoicesData.list.length < pageSize}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};